import React, { Suspense, lazy } from "react";
import { createBrowserRouter, RouteObject } from "react-router-dom";
import PageLayout from "@/components/Layout/PageLayout";

// Lazy-loaded page components
const Home = lazy(() => import("@/pages/Home/Home"));
const HomeEs = lazy(() => import("@/pages/Home/HomeEs"));
const About = lazy(() => import("@/pages/About/About"));
const AboutEs = lazy(() => import("@/pages/About/AboutEs"));
const Contact = lazy(() => import("@/pages/Contact/Contact"));
const ContactEs = lazy(() => import("@/pages/Contact/ContactEs"));
const NotFound = lazy(() => import("@/pages/NotFound/NotFound"));

// Hire Me (for recruiters)
const HireMe = lazy(() => import("@/pages/HireMe/HireMe"));

// Legal Pages
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy/PrivacyPolicy"));
const PrivacyPolicyEs = lazy(
  () => import("@/pages/PrivacyPolicy/PrivacyPolicyEs")
);
const TermsOfService = lazy(
  () => import("@/pages/TermsOfService/TermsOfService")
);
const TermsOfServiceEs = lazy(
  () => import("@/pages/TermsOfService/TermsOfServiceEs")
);

// Services Hub
const Services = lazy(() => import("@/pages/Services/Services"));
const ServicesEs = lazy(() => import("@/pages/Services/ServicesEs"));

// Website Development
const WebsiteDevelopment = lazy(
  () => import("@/pages/Services/WebsiteDevelopment/WebsiteDevelopment")
);
const WebsiteDevelopmentEs = lazy(
  () => import("@/pages/Services/WebsiteDevelopment/WebsiteDevelopmentEs")
);

// Local SEO
const LocalSEO = lazy(() => import("@/pages/Services/LocalSEO/LocalSEO"));
const LocalSEOEs = lazy(() => import("@/pages/Services/LocalSEO/LocalSEOEs"));

// Technical SEO
const TechnicalSEO = lazy(
  () => import("@/pages/Services/TechnicalSEO/TechnicalSEO")
);
const TechnicalSEOEs = lazy(
  () => import("@/pages/Services/TechnicalSEO/TechnicalSEOEs")
);

// Google Business Profile
const GoogleBusinessProfile = lazy(
  () => import("@/pages/Services/GoogleBusinessProfile/GoogleBusinessProfile")
);
const GoogleBusinessProfileEs = lazy(
  () => import("@/pages/Services/GoogleBusinessProfile/GoogleBusinessProfileEs")
);

// Social Media Management
const SocialMediaManagement = lazy(
  () => import("@/pages/Services/SocialMediaManagement/SocialMediaManagement")
);
const SocialMediaManagementEs = lazy(
  () => import("@/pages/Services/SocialMediaManagement/SocialMediaManagementEs")
);

// Digital Marketing
const DigitalMarketing = lazy(
  () => import("@/pages/Services/DigitalMarketing/DigitalMarketing")
);
const DigitalMarketingEs = lazy(
  () => import("@/pages/Services/DigitalMarketing/DigitalMarketingEs")
);

// Episodes (Streaming)
const Episodes = lazy(() => import("@/pages/Episodes/Episodes"));
const EpisodesEs = lazy(() => import("@/pages/Episodes/EpisodesEs"));

// Loading component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-burgundy-900 border-t-transparent" />
  </div>
);

// Wrap components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.FC>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// Route definitions
const routes: RouteObject[] = [
  {
    element: <PageLayout />,
    children: [
      // English routes
      { path: "/", element: withSuspense(Home) },
      { path: "/about", element: withSuspense(About) },
      { path: "/contact", element: withSuspense(Contact) },
      { path: "/hire-me", element: withSuspense(HireMe) },

      // Legal Pages (English)
      { path: "/privacy-policy", element: withSuspense(PrivacyPolicy) },
      { path: "/terms-of-service", element: withSuspense(TermsOfService) },

      // Episodes
      { path: "/episodes", element: withSuspense(Episodes) },

      // English Service Routes
      { path: "/services", element: withSuspense(Services) },
      {
        path: "/services/website-development",
        element: withSuspense(WebsiteDevelopment),
      },
      { path: "/services/local-seo", element: withSuspense(LocalSEO) },
      { path: "/services/technical-seo", element: withSuspense(TechnicalSEO) },
      {
        path: "/services/google-business-profile",
        element: withSuspense(GoogleBusinessProfile),
      },
      {
        path: "/services/social-media-management",
        element: withSuspense(SocialMediaManagement),
      },
      {
        path: "/services/digital-marketing",
        element: withSuspense(DigitalMarketing),
      },

      // Spanish Episodes
      { path: "/es/episodios", element: withSuspense(EpisodesEs) },

      // Spanish routes
      { path: "/es", element: withSuspense(HomeEs) },
      { path: "/es/sobre-nosotros", element: withSuspense(AboutEs) },
      { path: "/es/contacto", element: withSuspense(ContactEs) },

      // Legal Pages (Spanish)
      {
        path: "/es/politica-de-privacidad",
        element: withSuspense(PrivacyPolicyEs),
      },
      {
        path: "/es/terminos-de-servicio",
        element: withSuspense(TermsOfServiceEs),
      },

      // Spanish Service Routes
      { path: "/es/servicios", element: withSuspense(ServicesEs) },
      {
        path: "/es/servicios/desarrollo-web",
        element: withSuspense(WebsiteDevelopmentEs),
      },
      { path: "/es/servicios/seo-local", element: withSuspense(LocalSEOEs) },
      {
        path: "/es/servicios/seo-tecnico",
        element: withSuspense(TechnicalSEOEs),
      },
      {
        path: "/es/servicios/google-business-profile",
        element: withSuspense(GoogleBusinessProfileEs),
      },
      {
        path: "/es/servicios/gestion-redes-sociales",
        element: withSuspense(SocialMediaManagementEs),
      },
      {
        path: "/es/servicios/marketing-digital",
        element: withSuspense(DigitalMarketingEs),
      },

      // 404
      { path: "*", element: withSuspense(NotFound) },
    ],
  },
];

export const router = createBrowserRouter(routes);

// Export route paths for type safety
export const routePaths = {
  // English
  home: "/",
  about: "/about",
  contact: "/contact",
  hireMe: "/hire-me",
  privacyPolicy: "/privacy-policy",
  termsOfService: "/terms-of-service",
  services: "/services",
  websiteDevelopment: "/services/website-development",
  localSeo: "/services/local-seo",
  technicalSeo: "/services/technical-seo",
  googleBusinessProfile: "/services/google-business-profile",
  socialMediaManagement: "/services/social-media-management",
  digitalMarketing: "/services/digital-marketing",
  episodes: "/episodes",

  // Spanish
  homeEs: "/es",
  aboutEs: "/es/sobre-nosotros",
  contactEs: "/es/contacto",
  privacyPolicyEs: "/es/politica-de-privacidad",
  termsOfServiceEs: "/es/terminos-de-servicio",
  serviciosEs: "/es/servicios",
  desarrolloWebEs: "/es/servicios/desarrollo-web",
  seoLocalEs: "/es/servicios/seo-local",
  seoTecnicoEs: "/es/servicios/seo-tecnico",
  googleBusinessProfileEs: "/es/servicios/google-business-profile",
  gestionRedesSocialesEs: "/es/servicios/gestion-redes-sociales",
  marketingDigitalEs: "/es/servicios/marketing-digital",
  episodiosEs: "/es/episodios",
} as const;
